/* ==========================================================================
   Mairaz - Extended Size Upgrade   (assets/mairaz-extended-size.js)
   --------------------------------------------------------------------------
   Keeps the cart holding exactly ONE "Extended Size Upgrade" unit for every
   qualifying XL / XXL / XXXL Standard Stitched or Custom Stitched article in
   the cart.

   TWO PRICE TIERS (22 Oct 2026)
     XL           -> the $30 variant  (CFG.xlVariantId)
     XXL / XXXL   -> the $60 variant  (CFG.variantId, 67479159341360, unchanged)
   Each tier is its own cart line and is counted separately, so a mixed cart
   (one XL and one XXL dress) holds one $30 line and one $60 line.
   If the config does not supply xlVariantId the script behaves exactly as it
   did before: every XL / XXL / XXXL article takes the $60 line. Any other
   variant of the Extended Size Upgrade product found in the cart (for example
   a $30 line left behind after a rollback) is removed.

   QUALIFYING RULE (both must be true)
     TYPE : Standard Stitched  OR  Custom Stitched
     SIZE : XL / XXL / XXXL  (also 1XL, 2XL, 3XL, X-Large, XX-Large,
            XXX-Large, Extra Large, Double XL, Triple XL)

   NEVER QUALIFIES
     XS, S, M, L, Large, One Size, Ready Shawl, Unstitched,
     and the Extended Size Upgrade / Express Delivery Upgrade lines themselves.

   This file is completely independent of assets/mairaz-express-fee.js and
   must never modify the Express Delivery Upgrade line.
   ========================================================================== */
(function () {
  'use strict';

  var CFG = window.mairazExtendedSize;
  if (!CFG || window.mairazExtendedSizeReady) return;
  window.mairazExtendedSizeReady = true;

  function log(message, detail) {
    if (window.console && window.console.warn) {
      window.console.warn('[mairaz-extended-size] ' + message, detail === undefined ? '' : detail);
    }
  }

  var VARIANT_ID = Number(CFG.variantId);
  if (!CFG.variantId || !isFinite(VARIANT_ID) || VARIANT_ID <= 0) {
    log('variantId is missing or not a usable numeric variant id; the Extended Size Upgrade is INACTIVE.', CFG.variantId);
    return;
  }

  /* XL tier is optional. Without it, XL falls back to the $60 line. */
  var XL_VARIANT_ID = Number(CFG.xlVariantId);
  var HAS_XL_TIER = !!CFG.xlVariantId && isFinite(XL_VARIANT_ID) && XL_VARIANT_ID > 0 && XL_VARIANT_ID !== VARIANT_ID;
  if (!HAS_XL_TIER) XL_VARIANT_ID = 0;

  /* Tier keys: 'xl' = $30 line, 'xxl' = $60 line (XXL and XXXL). */
  var TIER_KEYS = HAS_XL_TIER ? ['xl', 'xxl'] : ['xxl'];
  var TIER_VARIANT = { xl: XL_VARIANT_ID, xxl: VARIANT_ID };

  var nativeFetch = (typeof window.fetch === 'function') ? window.fetch.bind(window) : null;
  if (!nativeFetch || typeof window.Promise !== 'function') {
    log('fetch/Promise unavailable; the Extended Size Upgrade is INACTIVE.');
    return;
  }

  var R = CFG.routes || {};
  var CART_JS = (R.cart || '/cart') + '.js';
  var ADD_JS = (R.add || '/cart/add') + '.js';
  var CHANGE_JS = (R.change || '/cart/change') + '.js';

  var DEBOUNCE_MS = 150;
  var FETCH_TIMEOUT_MS = 10000;
  var MAX_WRITES_PER_CYCLE = 8;
  var MAX_CONSECUTIVE_FAILURES = 3;
  var DISABLED_FOR_MS = 30000;
  var CHECKOUT_MAX_WAIT_MS = 6000;

  function nowMs() { return Date.now ? Date.now() : new Date().getTime(); }
  function has(obj, key) { return Object.prototype.hasOwnProperty.call(obj, key); }

  /* ------------------------------------------------------------------------
     1. QUALIFICATION
     --------------------------------------------------------------------- */

  var SIZE_ALIASES = {
    'XL': 1, '1XL': 1, 'XLARGE': 1, 'EXTRALARGE': 1,
    'XXL': 1, '2XL': 1, 'XXLARGE': 1, '2XLARGE': 1,
    'DOUBLEXL': 1, 'DOUBLEXLARGE': 1, 'DOUBLEEXTRALARGE': 1,
    'XXXL': 1, '3XL': 1, 'XXXLARGE': 1, '3XLARGE': 1,
    'TRIPLEXL': 1, 'TRIPLEXLARGE': 1, 'TRIPLEEXTRALARGE': 1
  };

  var SIZE_NEVER = {
    '': 1, 'XS': 1, 'S': 1, 'M': 1, 'L': 1,
    'XSMALL': 1, 'EXTRASMALL': 1, 'SMALL': 1, 'MEDIUM': 1, 'LARGE': 1,
    'ONESIZE': 1, 'FREESIZE': 1, 'READYSHAWL': 1, 'UNSTITCHED': 1,
    'STANDARD': 1, 'DEFAULT': 1, 'DEFAULTTITLE': 1,
    'CHOOSESIZE': 1, 'SELECTSIZE': 1, 'NA': 1, 'NONE': 1
  };

  /* Sizes that take the $30 XL tier. Every other qualifying size takes $60. */
  var SIZE_XL_TIER = { 'XL': 1, '1XL': 1, 'XLARGE': 1, 'EXTRALARGE': 1 };

  var TYPE_QUALIFIES = { 'STANDARDSTITCHED': 1, 'CUSTOMSTITCHED': 1 };
  var SERVICE_PRODUCT_TYPES = { 'SIZINGSERVICE': 1, 'DELIVERYSERVICE': 1 };

  function squash(value) {
    if (value === null || value === undefined) return '';
    return String(value).toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  function sizeQualifies(rawSize) {
    var key = squash(rawSize);
    if (!key) return false;
    if (has(SIZE_NEVER, key)) return false;
    return has(SIZE_ALIASES, key);
  }

  /* 'xl', 'xxl' or null. XL only maps to 'xl' when the XL tier is configured. */
  function sizeTier(rawSize) {
    if (!sizeQualifies(rawSize)) return null;
    if (HAS_XL_TIER && has(SIZE_XL_TIER, squash(rawSize))) return 'xl';
    return 'xxl';
  }

  function typeQualifies(rawType) {
    var key = squash(rawType);
    if (!key) return false;
    return has(TYPE_QUALIFIES, key);
  }

  function typeQualifiesFromTitle(title) {
    var parts = String(title === null || title === undefined ? '' : title).split('/');
    for (var i = 0; i < parts.length; i++) {
      if (typeQualifies(parts[i])) return true;
    }
    return false;
  }

  function propertyValue(properties, wantedKey) {
    if (!properties) return '';
    for (var key in properties) {
      if (!has(properties, key)) continue;
      if (String(key).toLowerCase().replace(/[^a-z]/g, '') === wantedKey) return properties[key];
    }
    return '';
  }

  function lineTypeValue(item) {
    var values = item.options_with_values || [];
    for (var i = 0; i < values.length; i++) {
      var name = String(values[i].name || '').toLowerCase().replace(/[^a-z]/g, '');
      if (name === 'type') return values[i].value;
    }
    return null;
  }

  /* 'xl' / 'xxl' for a line that IS one of our managed upgrade variants. */
  function surchargeTier(item) {
    var id = Number(item && item.variant_id);
    if (id === VARIANT_ID) return 'xxl';
    if (HAS_XL_TIER && id === XL_VARIANT_ID) return 'xl';
    return null;
  }

  function isSurchargeLine(item) {
    return surchargeTier(item) !== null;
  }

  /* Any other variant of the upgrade product (e.g. after a rollback). */
  function isStrayUpgradeLine(item) {
    if (!item || isSurchargeLine(item)) return false;
    return !!CFG.productHandle && String(item.handle || '') === String(CFG.productHandle);
  }

  function lineQualifies(item) {
    if (!item) return false;
    if (isSurchargeLine(item)) return false;
    if (CFG.productHandle && String(item.handle || '') === String(CFG.productHandle)) return false;
    if (has(SERVICE_PRODUCT_TYPES, squash(item.product_type))) return false;
    if (!sizeQualifies(propertyValue(item.properties, 'size'))) return false;
    var typeValue = lineTypeValue(item);
    if (typeValue === null) return typeQualifiesFromTitle(item.variant_title);
    return typeQualifies(typeValue);
  }

  function lineTier(item) {
    if (!lineQualifies(item)) return null;
    return sizeTier(propertyValue(item.properties, 'size'));
  }

  /* ------------------------------------------------------------------------
     2. NETWORK (uses the pre-hook fetch so our own writes never re-trigger us)
     --------------------------------------------------------------------- */

  function requestJSON(url, options) {
    options = options || {};
    var init = {
      method: options.method || 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    };
    if (options.body !== undefined) {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(options.body);
    }
    return new Promise(function (resolve, reject) {
      var settled = false;
      var timer = window.setTimeout(function () {
        if (settled) return;
        settled = true;
        reject(new Error('timeout'));
      }, FETCH_TIMEOUT_MS);
      nativeFetch(url, init).then(function (response) {
        return response.text().then(function (text) {
          var data = null;
          try { data = text ? JSON.parse(text) : null; } catch (parseError) { data = null; }
          if (!response.ok) {
            var httpError = new Error('HTTP ' + response.status);
            httpError.data = data;
            throw httpError;
          }
          return data;
        });
      }).then(function (data) {
        if (settled) return;
        settled = true; window.clearTimeout(timer); resolve(data);
      }, function (error) {
        if (settled) return;
        settled = true; window.clearTimeout(timer); reject(error);
      });
    });
  }

  /* ------------------------------------------------------------------------
     3. CART STATE - the real Shopify cart is the single source of truth
     --------------------------------------------------------------------- */

  var lastState = null;

  function readCartState() {
    return requestJSON(CART_JS, { method: 'GET' }).then(function (cart) {
      var items = (cart && cart.items) || [];
      var tiers = {};
      for (var t = 0; t < TIER_KEYS.length; t++) {
        tiers[TIER_KEYS[t]] = { required: 0, current: 0, lines: [] };
      }
      var stray = [];
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        var quantity = Number(item.quantity) || 0;
        var held = surchargeTier(item);
        if (held) {
          tiers[held].lines.push({ key: item.key, quantity: quantity });
          tiers[held].current += quantity;
          continue;
        }
        if (isStrayUpgradeLine(item)) {
          stray.push({ key: item.key, quantity: quantity });
          continue;
        }
        var needed = lineTier(item);
        if (needed) tiers[needed].required += quantity;
      }
      var required = 0;
      var current = 0;
      for (var k = 0; k < TIER_KEYS.length; k++) {
        required += tiers[TIER_KEYS[k]].required;
        current += tiers[TIER_KEYS[k]].current;
      }
      lastState = {
        itemCount: (cart && cart.item_count) || 0,
        required: required,
        current: current,
        tiers: tiers,
        stray: stray
      };
      return lastState;
    });
  }

  /* ------------------------------------------------------------------------
     4. SYNCHRONISATION ENGINE (single in-flight cycle, debounced, self-healing)
     --------------------------------------------------------------------- */

  var busy = false;
  var requeue = false;
  var debounceTimer = null;
  var pending = null;
  var failures = 0;
  var disabledUntil = 0;
  var verified = false;

  function scheduleSync(reason) {
    verified = false;
    if (debounceTimer) return pending || Promise.resolve(false);
    debounceTimer = window.setTimeout(function () {
      debounceTimer = null;
      runSync(reason);
    }, DEBOUNCE_MS);
    return pending || Promise.resolve(false);
  }

  function syncNow(reason) {
    if (debounceTimer) { window.clearTimeout(debounceTimer); debounceTimer = null; }
    return runSync(reason || 'manual');
  }

  function runSync(reason) {
    if (nowMs() < disabledUntil) return Promise.resolve(false);
    if (busy) { requeue = true; return pending || Promise.resolve(false); }

    busy = true;
    verified = false;
    setLocked(true);

    var writes = 0;
    var changed = false;

    /* One write per pass, then re-read the real cart. Removals and decreases
       go first so the cart never briefly holds more upgrade units than it
       needs. */
    function pass() {
      return readCartState().then(function (state) {
        var write = null;
        var detail = null;

        if (state.stray.length) {
          detail = { stray: state.stray[0].key };
          write = function () {
            return requestJSON(CHANGE_JS, { method: 'POST', body: { id: state.stray[0].key, quantity: 0 } });
          };
        }

        for (var d = 0; !write && d < TIER_KEYS.length; d++) {
          var dupLines = state.tiers[TIER_KEYS[d]].lines;
          if (dupLines.length > 1) {
            detail = { duplicate: TIER_KEYS[d] };
            write = (function (key) {
              return function () { return requestJSON(CHANGE_JS, { method: 'POST', body: { id: key, quantity: 0 } }); };
            })(dupLines[dupLines.length - 1].key);
          }
        }

        for (var direction = 0; !write && direction < 2; direction++) {
          for (var t = 0; !write && t < TIER_KEYS.length; t++) {
            var tierKey = TIER_KEYS[t];
            var tier = state.tiers[tierKey];
            var line = tier.lines[0] || null;
            var currentQuantity = line ? line.quantity : 0;
            if (currentQuantity === tier.required) continue;
            var shrinking = currentQuantity > tier.required;
            if ((direction === 0) !== shrinking) continue;
            detail = { tier: tierKey, required: tier.required, inCart: currentQuantity };
            write = (function (line, tierKey, quantity) {
              return function () {
                if (line) return requestJSON(CHANGE_JS, { method: 'POST', body: { id: line.key, quantity: quantity } });
                return requestJSON(ADD_JS, { method: 'POST', body: { items: [{ id: TIER_VARIANT[tierKey], quantity: quantity }] } });
              };
            })(line, tierKey, tier.required);
          }
        }

        if (!write) return true;

        if (writes >= MAX_WRITES_PER_CYCLE) {
          log('the cart would not accept the required Extended Size Upgrade quantity.', detail);
          return false;
        }
        writes += 1;
        return write().then(function () { changed = true; return pass(); });
      });
    }

    pending = pass().then(function (ok) {
      busy = false;
      failures = 0;
      verified = !!ok;
      setLocked(false);
      if (changed) { refreshCartUI(); } else { decorateCart(); }
      if (requeue) { requeue = false; return runSync('requeue'); }
      return verified;
    }, function (error) {
      busy = false;
      verified = false;
      failures += 1;
      if (failures >= MAX_CONSECUTIVE_FAILURES) {
        disabledUntil = nowMs() + DISABLED_FOR_MS;
        log('paused for ' + (DISABLED_FOR_MS / 1000) + 's after repeated cart failures.', error && error.message);
      }
      setLocked(false);
      if (requeue) { requeue = false; return runSync('requeue'); }
      return false;
    });

    return pending;
  }

  /* ------------------------------------------------------------------------
     5. CART UI REFRESH (Shopify Section Rendering API, Dawn-compatible)
     --------------------------------------------------------------------- */

  var suppressObserver = 0;

  function sectionTargets() {
    var targets = [];
    var items = document.getElementById('main-cart-items');
    if (items && items.getAttribute('data-id')) {
      targets.push({ id: 'main-cart-items', section: items.getAttribute('data-id'), selector: '.js-contents' });
    }
    var footer = document.getElementById('main-cart-footer');
    if (footer && footer.getAttribute('data-id')) {
      targets.push({ id: 'main-cart-footer', section: footer.getAttribute('data-id'), selector: '.js-contents' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      targets.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }
    if (document.getElementById('cart-live-region-text')) {
      targets.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
    }
        if (document.getElementById('cart-notification-button')) {
      targets.push({ id: 'cart-notification-button', section: 'cart-notification-button', selector: '.shopify-section' });
    }
    return targets;
  }

  function sectionsUrl(names) {
    var separator = location.search ? '&' : '?';
    return location.pathname + location.search + separator + 'sections=' + encodeURIComponent(names.join(','));
  }

  function refreshCartUI() {
    var targets = sectionTargets();
    if (!targets.length) { decorateCart(); return Promise.resolve(); }

    var names = [];
    for (var i = 0; i < targets.length; i++) names.push(targets[i].section);

    suppressObserver += 1;
    return nativeFetch(sectionsUrl(names), { credentials: 'same-origin' }).then(function (response) {
      return response.json();
    }).then(function (sections) {
      var parser = new DOMParser();
      for (var j = 0; j < targets.length; j++) {
        var target = targets[j];
        var html = sections[target.section];
        if (!html) continue;
        var host = document.getElementById(target.id);
        if (!host) continue;
        var parsed = parser.parseFromString(html, 'text/html');
        var source = parsed.querySelector(target.selector);
        if (!source) continue;
        var destination = host.querySelector(target.selector) || host;
        destination.innerHTML = source.innerHTML;
      }
      applyEmptyState();
      decorateCart();
    }).catch(function (error) {
      log('cart UI refresh failed.', error && error.message);
      decorateCart();
    }).then(function () {
      window.setTimeout(function () { suppressObserver = Math.max(0, suppressObserver - 1); }, 400);
    });
  }

  function applyEmptyState() {
    if (!lastState) return;
    var empty = lastState.itemCount === 0;
    var cartElement = document.querySelector('cart-items') || document.querySelector('.cart');
    if (cartElement && cartElement.classList) cartElement.classList.toggle('is-empty', empty);
    var footer = document.getElementById('main-cart-footer');
    if (footer && footer.classList) footer.classList.toggle('is-empty', empty);
  }

  /* ------------------------------------------------------------------------
     6. CART LINE PRESENTATION
     --------------------------------------------------------------------- */

  var STYLE_ID = 'mairaz-extended-size-style';

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.appendChild(document.createTextNode([
      '.mrz-esu-line .cart-item__quantity-wrapper quantity-input,',
      '.mrz-esu-line .cart-item__quantity-wrapper quantity-popover-container,',
      '.mrz-esu-line quantity-input,',
      '.mrz-esu-line cart-remove-button { display: none !important; }',
      '.mrz-esu-line .mrz-esu-qty { display: block; font-size: 0.9em; opacity: 0.85; white-space: nowrap; }',
      '.mrz-esu-line .mrz-esu-note { display: block; font-size: 0.9em; opacity: 0.85; margin-top: 0.2rem; }',
      '.mrz-esu-syncing { pointer-events: none !important; opacity: 0.55 !important; }',
      /* Product page size note: opens by easing its height from 0 and fading
         in, so content below glides instead of jumping. */
      '.mrz-esu-size-note { display: grid; grid-template-rows: 0fr; opacity: 0; transform: translateY(-4px); margin: 0;',
      '  transition: grid-template-rows .28s ease, opacity .28s ease, transform .28s ease, margin .28s ease; }',
      '.mrz-esu-size-note.is-on { grid-template-rows: 1fr; opacity: 1; transform: none; margin: 0 0 1.2rem; }',
      '.mrz-esu-size-note__inner { overflow: hidden; min-height: 0; }',
      '.mrz-esu-size-note__text { display: inline-block; margin-top: 0.2rem; padding: 0.6rem 1.2rem; border-radius: 999px;',
      '  background: rgba(96, 18, 24, 0.06); color: rgb(96, 18, 24); font-size: 1.25rem; line-height: 1.4; letter-spacing: 0.01em; }',
      '.mrz-esu-size-note__text.is-swap { animation: mrzEsuSwap .22s ease; }',
      '@keyframes mrzEsuSwap { from { opacity: 0.25; } to { opacity: 1; } }',
      '@media (prefers-reduced-motion: reduce) {',
      '  .mrz-esu-size-note { transition: none; transform: none; }',
      '  .mrz-esu-size-note__text.is-swap { animation: none; }',
      '}'
    ].join('\n')));
    (document.head || document.documentElement).appendChild(style);
  }

  /* Returns [{ row, tier }] for every upgrade line rendered on the page. */
  function surchargeRows() {
    var rows = [];
    var seen = [];
    var links = document.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) {
      var href = links[i].getAttribute('href') || '';
      if (href.indexOf('/cart/change') < 0 && href.indexOf('/products/') < 0) continue;
      var tier = null;
      for (var t = 0; t < TIER_KEYS.length; t++) {
        if (href.indexOf(String(TIER_VARIANT[TIER_KEYS[t]])) > -1) { tier = TIER_KEYS[t]; break; }
      }
      if (!tier) continue;
      if (!links[i].closest) continue;
      var row = links[i].closest('.cart-item, tr.cart-item, .cart-notification-product');
      if (row && seen.indexOf(row) < 0) { seen.push(row); rows.push({ row: row, tier: tier }); }
    }
    return rows;
  }

  function lineSubLabelFor(tier) {
    if (tier === 'xl') return CFG.xlLineSubLabel || 'For XL sizing';
    if (HAS_XL_TIER) return CFG.xxlLineSubLabel || 'For XXL and XXXL sizing';
    return CFG.lineSubLabel || 'For XL, XXL and XXXL sizing';
  }

  function decorateCart() {
    suppressObserver += 1;
    window.setTimeout(function () { suppressObserver = Math.max(0, suppressObserver - 1); }, 0);
    injectStyles();
    var rows = surchargeRows();
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i].row;
      var rowTier = rows[i].tier;
      if (row.classList) row.classList.add('mrz-esu-line');

      var controls = row.querySelectorAll('input[name="updates[]"], quantity-input input, .quantity__button');
      for (var j = 0; j < controls.length; j++) {
        controls[j].setAttribute('disabled', 'disabled');
        controls[j].setAttribute('tabindex', '-1');
        controls[j].setAttribute('aria-hidden', 'true');
      }

      var removers = row.querySelectorAll('cart-remove-button a, cart-remove-button button');
      for (var k = 0; k < removers.length; k++) {
        removers[k].setAttribute('tabindex', '-1');
        removers[k].setAttribute('aria-hidden', 'true');
        removers[k].removeAttribute('href');
      }

      var details = row.querySelector('.cart-item__details') || row;
      if (details && !details.querySelector('.mrz-esu-note')) {
        var note = document.createElement('span');
        note.className = 'mrz-esu-note';
        note.appendChild(document.createTextNode(lineSubLabelFor(rowTier)));
        var anchor = details.querySelector('.cart-item__title');
        if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(note, anchor.nextSibling);
        else details.appendChild(note);
      }

      var quantityCell = row.querySelector('.cart-item__quantity');
      if (quantityCell && lastState && lastState.tiers && lastState.tiers[rowTier]) {
        var readout = quantityCell.querySelector('.mrz-esu-qty');
        if (!readout) {
          readout = document.createElement('span');
          readout.className = 'mrz-esu-qty';
          quantityCell.appendChild(readout);
        }
        var readoutText = 'Qty ' + (lastState.tiers[rowTier].current || 0);
        if (readout.textContent !== readoutText) readout.textContent = readoutText;
      }
    }
  }

  /* ------------------------------------------------------------------------
     7. CHECKOUT PROTECTION
     --------------------------------------------------------------------- */

  var CHECKOUT_SELECTOR = 'button[name="checkout"], input[name="checkout"], #checkout, .cart__checkout-button';
  var ACCELERATED_SELECTOR = '.cart__dynamic-checkout-buttons, .additional-checkout-buttons, .shopify-payment-button';

  function setLocked(locked) {
    var nodes = document.querySelectorAll(CHECKOUT_SELECTOR + ', ' + ACCELERATED_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].classList) continue;
      if (locked) nodes[i].classList.add('mrz-esu-syncing');
      else nodes[i].classList.remove('mrz-esu-syncing');
    }
  }

  function isSynchronised() {
    return verified === true && busy === false && debounceTimer === null;
  }

  window.addEventListener('click', function (event) {
    var origin = event.target;
    if (!origin || !origin.closest) return;
    var button = origin.closest(CHECKOUT_SELECTOR);
    if (!button) return;

    if (button.getAttribute('data-mrz-esu-go') === '1') {
      button.removeAttribute('data-mrz-esu-go');
      return;
    }
    // Always re-validate against the live Shopify cart before checkout proceeds.

    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();

    var released = false;
    function release() {
      if (released) return;
      released = true;
      button.setAttribute('data-mrz-esu-go', '1');
      if (typeof button.click === 'function') button.click();
      else if (button.form && typeof button.form.submit === 'function') button.form.submit();
    }
    window.setTimeout(release, CHECKOUT_MAX_WAIT_MS);
    syncNow('checkout').then(release, release);
  }, true);

  /* ------------------------------------------------------------------------
     8. DETECT EVERY CART MUTATION MADE BY THE THEME OR THE CUSTOMER
     --------------------------------------------------------------------- */

  var CART_WRITE_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function isCartWrite(url) {
    try { return CART_WRITE_PATTERN.test(String(url)); } catch (error) { return false; }
  }

  (function hookFetch() {
    if (typeof window.fetch !== 'function') return;
    var previousFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var method = (init && init.method) || (input && input.method) || 'GET';
      var write = isCartWrite(url) && String(method).toUpperCase() === 'POST';
      var result = previousFetch.apply(this, arguments);
      if (write && result && typeof result.then === 'function') {
        result.then(function (response) {
          window.setTimeout(function () { syncNow('cart-write'); }, 0);
          return response;
        }, function (error) { return error; });
      }
      return result;
    };
  })();

  (function hookXHR() {
    if (!window.XMLHttpRequest || !window.XMLHttpRequest.prototype) return;
    var proto = window.XMLHttpRequest.prototype;
    var open = proto.open;
    var send = proto.send;
    if (typeof open !== 'function' || typeof send !== 'function') return;
    proto.open = function (method, url) {
      this.__mrzEsuWrite = isCartWrite(url) && String(method || '').toUpperCase() === 'POST';
      return open.apply(this, arguments);
    };
    proto.send = function () {
      var request = this;
      if (request.__mrzEsuWrite) {
        request.addEventListener('loadend', function () { syncNow('cart-write-xhr'); });
      }
      return send.apply(this, arguments);
    };
  })();

  function observeCart() {
    if (!window.MutationObserver) return;
    var observer = new MutationObserver(function () {
      if (suppressObserver > 0) return;
      decorateCart();
      scheduleSync('dom-change');
    });
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) observer.observe(bubble, { childList: true, subtree: true, characterData: true });
    var items = document.getElementById('main-cart-items');
    if (items) observer.observe(items, { childList: true, subtree: true });
    var notification = document.querySelector('cart-notification');
    if (notification) observer.observe(notification, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------------
     9. PRODUCT PAGE PRICE DISPLAY  (visual only - the real charge is the
        Extended Size Upgrade cart line)
     --------------------------------------------------------------------- */

  var PRODUCT = CFG.product || null;
  var priceTimer = null;

  function productForm() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      if (String(forms[i].className || '').indexOf('installment') > -1) continue;
      return forms[i];
    }
    return null;
  }

  function selectedVariantId() {
    var form = productForm();
    if (!form) return null;
    var field = form.querySelector('input[name="id"], select[name="id"]');
    return field ? String(field.value) : null;
  }

  function selectedSizeNode() {
    var nodes = document.querySelectorAll('[name="properties[Size]"]');
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.disabled) continue;
      if (node.type === 'radio' || node.type === 'checkbox') {
        if (node.checked) return node;
        continue;
      }
      return node;
    }
    return null;
  }

  function selectedSizeValue() {
    var node = selectedSizeNode();
    return node ? node.value : '';
  }

  /* ---- size note under the size buttons ---- */

  var NOTE_CLASS = 'mrz-esu-size-note';

  function noteTextFor(tier) {
    if (tier === 'xl') return CFG.noteXl || '';
    if (tier === 'xxl') return CFG.noteXxl || '';
    return '';
  }

  function noteFor(fieldset) {
    var next = fieldset.nextElementSibling;
    if (next && next.classList && next.classList.contains(NOTE_CLASS)) return next;
    var note = document.createElement('div');
    note.className = NOTE_CLASS;
    note.setAttribute('role', 'status');
    note.setAttribute('aria-live', 'polite');
    note.setAttribute('aria-hidden', 'true');
    var inner = document.createElement('div');
    inner.className = NOTE_CLASS + '__inner';
    var text = document.createElement('span');
    text.className = NOTE_CLASS + '__text';
    inner.appendChild(text);
    note.appendChild(inner);
    fieldset.parentNode.insertBefore(note, fieldset.nextSibling);
    return note;
  }

  function setNote(note, on, message) {
    var text = note.querySelector('.' + NOTE_CLASS + '__text');
    if (on) {
      if (text && text.textContent !== message) {
        var wasOn = note.classList.contains('is-on');
        text.textContent = message;
        if (wasOn) {
          text.classList.remove('is-swap');
          void text.offsetWidth;
          text.classList.add('is-swap');
        }
      }
      if (!note.classList.contains('is-on')) note.classList.add('is-on');
      if (note.getAttribute('aria-hidden') !== 'false') note.setAttribute('aria-hidden', 'false');
    } else {
      if (note.classList.contains('is-on')) note.classList.remove('is-on');
      if (note.getAttribute('aria-hidden') !== 'true') note.setAttribute('aria-hidden', 'true');
    }
  }

  /* Shows the note under the size row that holds the chosen size and hides
     every other one. Only writes to the DOM when something changes, so the
     product-info MutationObserver cannot loop. */
  function updateSizeNote(tier, sizeNode) {
    var message = noteTextFor(tier);
    var target = null;
    if (tier && message && sizeNode && sizeNode.closest) {
      target = sizeNode.closest('fieldset');
      if (target && !target.parentNode) target = null;
    }
    if (target) setNote(noteFor(target), true, message);
    var notes = document.querySelectorAll('.' + NOTE_CLASS);
    for (var i = 0; i < notes.length; i++) {
      if (target && notes[i] === target.nextElementSibling) continue;
      setNote(notes[i], false, '');
    }
  }

  function priceHost() {
    return document.querySelector('product-info [id^="price-"]') ||
           document.querySelector('.product__info-container [id^="price-"]') ||
           document.querySelector('.product__info-wrapper [id^="price-"]');
  }

  function overwrite(node, text) {
    if (!node) return;
    if (node.getAttribute('data-mrz-esu-base') === null) {
      node.setAttribute('data-mrz-esu-base', node.textContent);
    }
    node.textContent = text;
  }

  function restore(node) {
    var base = node.getAttribute('data-mrz-esu-base');
    if (base === null) return;
    node.textContent = base;
    node.removeAttribute('data-mrz-esu-base');
  }

  /* The sticky add-to-bag bar (sections/mairaz-theme.liquid) only re-reads the
     price when the Type option changes, so a size-only change (M -> XL,
     XL -> XXL) left it showing the previous price. Mirror the price it reads,
     using the same source element, whenever this script changes the price. */
  function syncStickyPrice() {
    var out = document.querySelector('[data-mz-sticky-live] [data-mz-sprice]');
    if (!out) return;
    var src = document.querySelector('.price__container .price-item--sale') ||
              document.querySelector('.price__container .price-item--regular') ||
              document.querySelector('.price-item--regular');
    if (!src) return;
    var text = String(src.textContent || '').replace(/\s+/g, ' ').trim();
    if (text && out.textContent !== text) out.textContent = text;
  }

  function updatePriceDisplay() {
    applyPriceDisplay();
    syncStickyPrice();
  }

  function applyPriceDisplay() {
    if (!PRODUCT || !PRODUCT.variants) return;
    var host = priceHost();
    if (!host) return;

    var marked = host.querySelectorAll('[data-mrz-esu-base]');
    var variantId = selectedVariantId();
    var data = variantId ? PRODUCT.variants[variantId] : null;
    var sizeNode = selectedSizeNode();
    var tier = (data && typeQualifies(data.type)) ? sizeTier(sizeNode ? sizeNode.value : '') : null;
    if (tier === 'xl' && !data.plusXl) tier = 'xxl';

    updateSizeNote(tier, sizeNode);

    if (!tier) {
      for (var i = 0; i < marked.length; i++) restore(marked[i]);
      host.removeAttribute('data-mrz-esu-applied');
      return;
    }

    var appliedKey = variantId + ':' + tier;
    if (host.getAttribute('data-mrz-esu-applied') === appliedKey && marked.length > 0) return;
    for (var j = 0; j < marked.length; j++) restore(marked[j]);

    var plus = tier === 'xl' ? data.plusXl : data.plus;
    var cmpPlus = tier === 'xl' ? data.cmpPlusXl : data.cmpPlus;

    var regular = host.querySelectorAll('.price__regular .price-item--regular');
    for (var k = 0; k < regular.length; k++) overwrite(regular[k], plus);

    var sale = host.querySelectorAll('.price__sale .price-item--sale');
    for (var m = 0; m < sale.length; m++) overwrite(sale[m], plus);

    if (data.hasCmp) {
      var struck = host.querySelectorAll('.price__sale s.price-item--regular');
      for (var n = 0; n < struck.length; n++) overwrite(struck[n], cmpPlus);
    }

    host.setAttribute('data-mrz-esu-applied', appliedKey);
  }

  function schedulePriceUpdate() {
    if (priceTimer) return;
    priceTimer = window.setTimeout(function () {
      priceTimer = null;
      updatePriceDisplay();
    }, 40);
  }

  function bindProductPage() {
    if (!PRODUCT) return;
    document.addEventListener('change', schedulePriceUpdate, true);
    document.addEventListener('input', schedulePriceUpdate, true);
    document.addEventListener('click', schedulePriceUpdate, true);

    if (window.MutationObserver) {
      var container = document.querySelector('product-info') ||
                      document.querySelector('.product__info-container');
      if (container) {
        new MutationObserver(function () { schedulePriceUpdate(); })
          .observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'disabled', 'value'] });
      }
    }

    /* Accelerated checkout must never skip the cart on a size-selectable
       product. There are currently no dynamic checkout buttons on product
       pages; this keeps the loophole closed if they are ever re-enabled. */
    if (document.querySelector('[name="properties[Size]"]')) {
      var accelerated = document.querySelectorAll('product-info .shopify-payment-button, .product__info-container .shopify-payment-button');
      for (var i = 0; i < accelerated.length; i++) {
        accelerated[i].style.display = 'none';
        accelerated[i].setAttribute('data-mrz-esu-hidden', 'true');
      }
    }
  }

  /* ------------------------------------------------------------------------
     10. BOOT
     --------------------------------------------------------------------- */

  function boot() {
    injectStyles();
    bindProductPage();
    observeCart();
    updatePriceDisplay();
    scheduleSync('page-load');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) syncNow('pageshow');
    schedulePriceUpdate();
  });

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) scheduleSync('visibility');
  });

  window.mairazExtendedSizeAPI = {
    sync: syncNow,
    state: function () { return lastState; },
    lineQualifies: lineQualifies,
    lineTier: lineTier,
    sizeQualifies: sizeQualifies,
    sizeTier: sizeTier,
    typeQualifies: typeQualifies,
    updatePriceDisplay: updatePriceDisplay,
    isSynchronised: isSynchronised,
    variantId: VARIANT_ID,
    xlVariantId: XL_VARIANT_ID || null
  };
})();
